// CANDIDATE OPTIONS for asset filter fields.
//
// Otto's normal option source is the filter-ai catalog view — one project-scoped load serving every
// dimension from memory. That view carries RECOMMENDATIONS vocabulary; asset fields ("asset_types.name",
// "assets.code") are not in it, so a lookup there returns [] and the resolver starves: Otto would report
// "I couldn't find that" for values that plainly exist on screen (the candidate-starvation class).
//
// Extending the catalog view is the eventual wide fix and needs a backend change + deploy. Until then this
// mirrors the panel's OWN call — `GET /assets/get_list`, the same endpoint, params and cascade that
// useMyFilters.handleFilterView builds — so Otto grounds against exactly the options the user would see if
// they opened that dropdown, including the faceting by every OTHER applied filter. Same precedent as
// dynamicOptions.js, which mirrors the dashboard's per-field call for the fields the catalog can't count.
//
// Best-effort by contract: any failure resolves to [] and the caller degrades to "couldn't verify", because
// an option list is never worth breaking a request over.
import * as Service from "./services";

// ------------------------------------------------------------------------------------------------------
// CACHE + IN-FLIGHT DEDUP. The dashboard pays ONE /filter_catalog load per scope and answers every field
// from memory; this source cannot (asset vocabulary is not in that view), so it costs one request per
// FIELD. That makes repetition the thing to eliminate: without this, every turn re-fetched every field it
// probed, and two fields resolving concurrently could issue the same request twice.
//
// Keyed on what actually changes the answer — the field, the search phrase, and the CASCADE (the other
// applied selections + scope), since narrowing the filters genuinely changes a field's option list. Any
// change to those is a different key, so a stale list can never be served for a filter state that moved on.
// Bounded and cleared per scope so a session cannot grow it without limit.
const MAX_CACHED = 300;
const cache = new Map(); // key -> { at, options }
const inflight = new Map(); // key -> Promise
const TTL_MS = 5 * 60 * 1000;

const now = () => (typeof performance !== "undefined" && performance.now ? performance.now() : 0);

// Key-order-stable JSON. NOT JSON.stringify(x, keys): that second argument is a REPLACER ALLOWLIST applied
// RECURSIVELY, so passing the top-level keys silently stripped every NESTED one — the selections themselves —
// and two different cascades hashed identically, serving one filter state's options for another.
const stableStringify = value => {
    if (value === null || typeof value !== "object") return JSON.stringify(value);
    if (Array.isArray(value)) return `[${value.map(stableStringify).join(",")}]`;
    return `{${Object.keys(value)
        .sort()
        .map(k => `${JSON.stringify(k)}:${stableStringify(value[k])}`)
        .join(",")}}`;
};

const cacheKeyOf = (field, search, params) => {
    // The cascade is what the request is actually conditioned on; stringify the params we send, minus the
    // parts already in the key, so any change to scope or applied filters yields a different entry.
    const { searchKey, field: _f, search_filters, ...rest } = params || {};
    let shape = "";
    try {
        shape = stableStringify(rest);
    } catch (e) {
        shape = String(Object.keys(rest || {}).length);
    }
    return `${field}\u001f${search || ""}\u001f${shape}`;
};

const readCache = key => {
    const hit = cache.get(key);
    if (!hit) return null;
    if (TTL_MS && now() - hit.at > TTL_MS) {
        cache.delete(key);
        return null;
    }
    return hit.options;
};

const writeCache = (key, options) => {
    if (cache.size >= MAX_CACHED) {
        // Cheapest sufficient eviction: drop the oldest inserted entry (Map preserves insertion order).
        const oldest = cache.keys().next().value;
        if (oldest !== undefined) cache.delete(oldest);
    }
    cache.set(key, { at: now(), options });
};

/** Test seam + a hook for a scope change (a different client) to drop everything. */
export const clearAssetOptionCache = () => {
    cache.clear();
    inflight.clear();
};

// Options come back as { success, list: [{ id?, name }] }; anything else is treated as no options rather
// than trusted, so a shape change degrades instead of injecting junk into the resolver.
const optionsFrom = res => {
    const data = (res && res.data) || {};
    if (!data.success) return [];
    const list = Array.isArray(data.list) ? data.list : [];
    return list.filter(o => o && o.name != null).map(o => ({ id: o.id, name: o.name }));
};

/**
 * Fetch one asset field's options.
 *
 * @param fieldMeta  Otto's dimension meta; `assetGetListKey` (the raw string) and `searchKey` come from
 *                   ottoAssetFilters.buildOttoAssetFilterFields.
 * @param search     value phrase to narrow by ("" = the field's full vocabulary)
 * @param context    { assetFilterContext: { currentFilters, prepareDropdownApiParams } } — supplied by the
 *                   mount so the cascade params are built by the PAGE, not reconstructed here.
 * @returns Promise<Array<{ id, name }>>
 */
export const fetchAssetOptions = async (fieldMeta, search, context = {}) => {
    const ctx = context.assetFilterContext || {};
    const field = fieldMeta && (fieldMeta.assetGetListKey || (Array.isArray(fieldMeta.get_list_field) ? fieldMeta.get_list_field[0] : null));
    if (!field) return [];

    const searchKey = (fieldMeta && fieldMeta.searchKey) || field;
    const current = ctx.currentFilters || {};

    // `list` is the OTHER dimensions' current selections — the cascade that narrows this field's options,
    // exactly as the panel's own dropdown fetch builds it. It must be read from `current.list`, NOT assembled
    // from "whatever keys are left over": every caller's currentFilters ALREADY carries a `list` key, so the
    // leftover approach nested the whole object inside itself
    //   list = { list: null, client_id: "…" }
    // which the endpoint rejects with a 500. Every field then returns no options and the resolver starves —
    // "show me only florida" on the Asset Dashboard hinted `region` correctly and still resolved to nothing
    // (trace 21bbd906: fields 38, starved 38). The assets GRID hid this because its
    // prepareDropdownApiParams rebuilds the params on the way out; the dashboard passes no such builder, so
    // the malformed shape reached the API unchanged.
    const { filters = {}, list: selections, limit, offset, has_image, verified_survey, ...scope } = current;
    const list = { ...(selections || {}) };
    // Drop THIS field's own selection from the cascade, exactly as the panel does: an option list narrowed
    // by the field's own current value can only ever return what is already picked.
    if (fieldMeta && fieldMeta.paramKey) delete list[fieldMeta.paramKey];

    const params = {
        ...scope,
        searchKey,
        field,
        filters,
        list,
        ...(search && String(search).trim() ? { search_filters: { [searchKey]: { key: search } } } : {})
    };

    const apiParams = typeof ctx.prepareDropdownApiParams === "function" ? ctx.prepareDropdownApiParams(params, searchKey) : params;

    // `fetchProbe` is the tracing seam catalog.fetchCandidates passes in (FTO-85): it emits the
    // `catalog.fetch` span for this source, and only THIS layer knows whether the answer came off its own
    // cache. Optional by contract — a source that ignores it is still traced, just always as uncached.
    const probe = context.fetchProbe;
    const markCached = () => {
        if (probe) probe.cached = true;
    };

    const key = cacheKeyOf(field, search, apiParams);
    const cached = readCache(key);
    if (cached) {
        markCached();
        return cached;
    }
    // DEDUP: two fields resolving concurrently, or a re-render mid-flight, must share one request rather
    // than racing identical ones to the backend.
    const pending = inflight.get(key);
    if (pending) {
        markCached();
        return pending;
    }

    const request = (async () => {
        try {
            const options = optionsFrom(await Service.getListForCommonFilter(apiParams));
            // Only cache a real answer. Caching [] would pin a failure (a 500, a dropped connection) for the
            // rest of the session and reproduce the starvation this source exists to avoid.
            if (options.length) writeCache(key, options);
            return options;
        } catch (e) {
            return [];
        } finally {
            inflight.delete(key);
        }
    })();
    inflight.set(key, request);
    return request;
};

/**
 * The candidate SOURCE Otto's catalog delegates to for this page (see catalog.fetchCandidates). Returned as
 * a stable object so the mount can memoize it — Otto's send callback depends on its identity.
 */
export const createAssetCandidateSource = ({ getCurrentFilters, prepareDropdownApiParams } = {}) => ({
    // `assets` matches the `optionSource` stamped on every asset field by the adapter.
    key: "assets",
    fetch: (fieldMeta, search, context) =>
        fetchAssetOptions(fieldMeta, search, {
            ...context,
            assetFilterContext: {
                currentFilters: typeof getCurrentFilters === "function" ? getCurrentFilters() : {},
                prepareDropdownApiParams
            }
        })
});
